/**
 * forecast/workbook.ts — the Combined Revenue Forecast workbook, as SHEET DATA.
 *
 * Moved from spa-cro/src/lib/forecast-workbook.ts by quick-260922-mvc so that
 * TWO writers produce byte-for-byte the same workbook contract:
 *   - the browser Download button (spa-cro, `write-excel-file/browser`), and
 *   - the sync-worker's weekly archive job (`write-excel-file/node`, Monday
 *     09:00 America/New_York — John, 2026-09-22).
 *
 * This module is PURE and dependency-free: it returns rows of cell objects
 * structurally compatible with `write-excel-file`'s `SheetData` (the writer is
 * chosen by the caller), and every dollar goes through `./weighting.js` — the
 * one source of truth for the tab — so the workbook's `Total Commit` ties to the
 * page's combined ARR EXACTLY (D-14; ~/forecast-artifact/tests/xlsx-test.js is
 * the reference contract: two sheets, whole dollars with a currency format, the
 * exec-sheet fills).
 */
import { fc, normalizeUpside, split, streams, type UpsidePct } from './weighting.js';

import type { ForecastDeal } from './schemas.js';

/**
 * D-14 / 05.1 D-14 — whole dollars with a currency number format. Money cells
 * are NUMBERS carrying this format, never a pre-formatted `"$1,361,500"`
 * string: a string neither sums nor sorts, which is the entire point of
 * shipping a workbook instead of a deck.
 */
export const FORECAST_WORKBOOK_MONEY_FORMAT = '"$"#,##0';

/** The file both writers produce. A constant, never caller-supplied (T-05.3-06-02). */
export const FORECAST_WORKBOOK_FILENAME = 'adeptia-combined-revenue-forecast.xlsx';

/**
 * D-13 — the four exec-sheet fills, re-derived from the reference workbook's
 * own `xl/styles.xml`. The palette lives HERE and only here; D-13 bands only
 * Closed-Won rows on the page, the committed / upside / totals banding is a
 * workbook affordance.
 */
export const FORECAST_WORKBOOK_PALETTE = {
  header: '#0070C0',
  headerText: '#FFFFFF',
  committed: '#D9EAD3',
  upside: '#C9DAF8',
  totals: '#FCE5CD',
} as const;

/**
 * One cell, structurally the subset of `write-excel-file`'s `CellObject` this
 * workbook uses. Declared here rather than imported so this package carries no
 * dependency on the writer; both writers accept it as-is.
 */
export interface ForecastWorkbookCell {
  value: string | number;
  type: typeof String | typeof Number;
  format?: string;
  fontWeight?: 'bold';
  backgroundColor?: string;
  textColor?: string;
}

export type ForecastWorkbookRow = (ForecastWorkbookCell | null)[];

/** The slice of a period payload the workbook actually needs. */
export interface ForecastWorkbookPeriod {
  period_label: string;
  arr: { deals: readonly ForecastDeal[] };
}

/** One built sheet, as `write-excel-file` describes it. */
export interface ForecastWorkbookSheet {
  sheet: string;
  data: ForecastWorkbookRow[];
  columns?: { width?: number }[];
}

/**
 * quick-260923-g5m — John, 2026-09-23, on the first Drive copy: "it needs close
 * date column … need the column for new or existing! … if a deal is managed
 * services being folded into the arr total, note that it is managed services
 * in the title … sorting is a. commit b. close date".
 */
/*
 * quick-261019-lvu — John, 2026-10-19: "i need the workbook downloads to include
 * New/Expansion, AE, Industry, Projected ARR, Month Close and Forecast Position …
 * projected arr = amount. and make the new/expansion/ms column." The same column
 * set, in the same order, as the CEO artifact's sheet (which adds no Weighted
 * ARR column); Projected ARR is the face-value Amount, Weighted ARR stays last
 * so the totals rows keep tying to the page.
 */
const HEADINGS = [
  'Deal',
  'New / Expansion / MS',
  'AE',
  'Industry',
  'Forecast Position',
  'Month Close',
  'Close Date',
  'Projected ARR',
  'Weighted ARR',
] as const;

/** Column index of the money figure the total rows carry (the last column). */
const WEIGHTED_COL = HEADINGS.length - 1;

const SUB_GROUP_MANAGED = 'Managed Services';

/** Sort rank: committed (Closed Won / Commit / Most Likely) → upside (Best Case) → the rest. */
function tierRank(d: ForecastDeal): number {
  if (d.tier === 'closed_won' || d.forecast_position === 'Closed Won') return 0;
  if (d.forecast_position === 'Commit' || d.forecast_position === 'Most Likely') return 0;
  if (d.forecast_position === 'Best Case') return 1;
  return 2;
}

/**
 * The sheet order John asked for: commit tier first, then by close date
 * (undated last), then by name so the order is total and stable.
 */
export function sortForWorkbook(deals: readonly ForecastDeal[]): ForecastDeal[] {
  return [...deals].sort((a, b) => {
    const r = tierRank(a) - tierRank(b);
    if (r !== 0) return r;
    if (a.close_date !== b.close_date) {
      if (a.close_date === null) return 1;
      if (b.close_date === null) return -1;
      return a.close_date.localeCompare(b.close_date);
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * quick-261015-lej (John: "make sure the deals under managed services have the
 * account name listed"). A Managed Services opportunity is usually named for
 * the engagement rather than the customer ("Managed Services -FY2027"), so its
 * row leads with the Salesforce Account name — the same "Account — deal" read
 * the CEO artifact prints. Null when the deal is Product, when the cache row
 * carries no account, or when the name already contains the account ("ACTIAN
 * Managed Services"), so nothing is printed twice. One rule for the /forecast
 * table and every workbook (Download, Monday archive, local weekly job).
 */
export function dealAccountPrefix(d: ForecastDeal): string | null {
  if (d.sub_group !== SUB_GROUP_MANAGED) return null;
  const account = (d.account_name ?? '').trim();
  if (account === '') return null;
  return d.name.toLowerCase().includes(account.toLowerCase()) ? null : account;
}

/**
 * The deal's title on the sheet. A Managed Services opportunity is folded into
 * the same ARR total as Product, so its row says so unless the Salesforce name
 * already does ("ACTIAN Managed Services"); it leads with the account when
 * `dealAccountPrefix` gives one (quick-261015-lej).
 */
export function workbookDealTitle(d: ForecastDeal): string {
  if (d.sub_group !== SUB_GROUP_MANAGED) return d.name;
  const prefix = dealAccountPrefix(d);
  const name = prefix === null ? d.name : `${prefix} — ${d.name}`;
  return /managed\s+services/i.test(d.name) ? name : `${name} (Managed Services)`;
}

/**
 * The New / Expansion / MS column (quick-261019-lvu, John: "make the
 * new/expansion/ms column"): the deal's ARR sub-group — New, Expansion, or MS
 * for Managed Services. Falls back to the Salesforce Type when the sub-group is
 * not one of the three; an em dash when neither says.
 */
export function workbookDealKind(d: Pick<ForecastDeal, 'sub_group' | 'type'>): string {
  if (d.sub_group === SUB_GROUP_MANAGED) return 'MS';
  if (d.sub_group === 'New' || d.type === 'New Business') return 'New';
  if (d.sub_group === 'Expansion' || d.type === 'Existing Business') return 'Expansion';
  return '—';
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * The Month Close column (quick-261019-lvu): the close month as "Oct 2026",
 * read straight off the ISO date so no timezone conversion can move it; an em
 * dash when the deal is undated or the date is malformed.
 */
export function workbookMonthClose(closeDate: string | null): string {
  if (closeDate === null) return '—';
  const m = /^(\d{4})-(\d{2})/.exec(closeDate);
  const year = m?.[1];
  const mm = m?.[2];
  if (year === undefined || mm === undefined) return '—';
  const month = MONTH_ABBR[Number(mm) - 1];
  return month === undefined ? '—' : `${month} ${year}`;
}

function headerCell(text: string): ForecastWorkbookCell {
  return {
    value: text,
    type: String,
    fontWeight: 'bold',
    backgroundColor: FORECAST_WORKBOOK_PALETTE.header,
    textColor: FORECAST_WORKBOOK_PALETTE.headerText,
  };
}

function moneyCell(amount: number, backgroundColor?: string, bold = false): ForecastWorkbookCell {
  return {
    value: amount,
    type: Number,
    format: FORECAST_WORKBOOK_MONEY_FORMAT,
    ...(bold ? { fontWeight: 'bold' as const } : {}),
    ...(backgroundColor === undefined ? {} : { backgroundColor }),
  };
}

function textCell(text: string, backgroundColor?: string, bold = false): ForecastWorkbookCell {
  return {
    value: text,
    type: String,
    ...(bold ? { fontWeight: 'bold' as const } : {}),
    ...(backgroundColor === undefined ? {} : { backgroundColor }),
  };
}

/**
 * The band a deal row carries, using the SAME classification `split()` tiers
 * by (05.1 D-21 / D-15). A Pipeline or blank-position deal gets NO fill and
 * still gets a row, carried at $0 (05.1 D-22).
 */
function bandFor(deal: ForecastDeal): string | undefined {
  if (deal.tier === 'closed_won' || deal.forecast_position === 'Closed Won') {
    return FORECAST_WORKBOOK_PALETTE.committed;
  }
  if (deal.forecast_position === 'Commit' || deal.forecast_position === 'Most Likely') {
    return FORECAST_WORKBOOK_PALETTE.committed;
  }
  if (deal.forecast_position === 'Best Case') {
    return FORECAST_WORKBOOK_PALETTE.upside;
  }
  return undefined;
}

function sheetFor(period: ForecastWorkbookPeriod, upsidePct: UpsidePct): ForecastWorkbookSheet {
  const deals = period.arr.deals;
  const tiers = split(deals, upsidePct);

  /** A totals row: label in the first column, figure under Weighted ARR. */
  function totalRow(label: string, amount: number): ForecastWorkbookRow {
    const row: ForecastWorkbookRow = Array.from({ length: HEADINGS.length }, () => null);
    row[0] = textCell(label, FORECAST_WORKBOOK_PALETTE.totals, true);
    row[WEIGHTED_COL] = moneyCell(amount, FORECAST_WORKBOOK_PALETTE.totals, true);
    return row;
  }

  const data: ForecastWorkbookRow[] = [
    HEADINGS.map((h) => headerCell(h)),
    ...sortForWorkbook(deals).map((d) => {
      const band = bandFor(d);
      return [
        textCell(workbookDealTitle(d), band),
        textCell(workbookDealKind(d), band),
        textCell(d.owner_name ?? '—', band),
        textCell(d.industry ?? '—', band),
        textCell(d.forecast_position, band),
        textCell(workbookMonthClose(d.close_date), band),
        // ISO date as text: sorts correctly in any locale and never drifts a
        // day through a timezone conversion on the way into the cell.
        textCell(d.close_date ?? '—', band),
        moneyCell(d.raw_amount, band),
        moneyCell(fc(d, upsidePct), band),
      ];
    }),
    totalRow('Committed Total', tiers.won + tiers.commit),
    totalRow('Upside Total', tiers.upside),
    // THE TIE. `streams().combinedArr` is the same call the summary strip
    // renders from, so the two are equal rather than approximately equal.
    totalRow('Total Commit', streams(deals, upsidePct).combinedArr),
  ];

  return {
    sheet: period.period_label,
    data,
    columns: [
      { width: 52 },
      { width: 20 },
      { width: 18 },
      { width: 22 },
      { width: 18 },
      { width: 12 },
      { width: 12 },
      { width: 14 },
      { width: 16 },
    ],
  };
}

/**
 * D-14 — build BOTH sheets, current quarter then next quarter. Sheet names come
 * from the payloads' own `period_label`, never a hard-coded quarter.
 */
export function buildWorkbook(
  cq: ForecastWorkbookPeriod,
  nq: ForecastWorkbookPeriod,
  upsidePct: UpsidePct,
): ForecastWorkbookSheet[] {
  const pct = normalizeUpside(upsidePct);
  return [sheetFor(cq, pct), sheetFor(nq, pct)];
}
